import React, { useState, useEffect } from 'react';
import { Download, TableProperties, AlertCircle } from 'lucide-react';

const StockStatement = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [fuelType, setFuelType] = useState('Petrol');
  const [openingBalance, setOpeningBalance] = useState('');

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const queryParams = new URLSearchParams({ month, year, fuelType });
      if (openingBalance) queryParams.append('openingBalance', openingBalance);
      const res = await fetch(`/api/reports/stock-statement-data?${queryParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const json = await res.json();
      if (json.error) {
        setError(json.error);
        setData(null);
      } else {
        setData(json);
      }
    } catch (err) {
      setError(err.message);
      setData(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [month, year, fuelType, openingBalance]);

  const handleExport = () => {
    const queryParams = new URLSearchParams({ month, year, fuelType });
    if (openingBalance) queryParams.append('openingBalance', openingBalance);
    window.open(`/api/export/stock-statement?${queryParams.toString()}`);
  };

  const renderTable = () => {
    if (!data) return null;
    const { tanks, statement, handlingLoss } = data;
    const isSingleTank = tanks.length === 1;

    let sumTotalInward = 0;
    let sumTotalOutward = 0;

    return (
      <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-sm bg-white mt-6">
        <table className="w-full text-sm text-left border-collapse min-w-[800px]">
          <thead className="bg-slate-50 text-slate-700 font-bold uppercase text-xs">
            {isSingleTank ? (
              <tr className="border-b border-slate-200">
                <th className="p-3 border-r border-slate-200 text-center">DATE</th>
                <th className="p-3 border-r border-slate-200 text-center bg-blue-50/50">OP. BALANCE</th>
                <th className="p-3 border-r border-slate-200 text-center">INWARD</th>
                <th className="p-3 border-r border-slate-200 text-center">OUTWARD</th>
                <th className="p-3 border-r border-slate-200 text-center bg-blue-100/50">CLOSING</th>
                <th className="p-3 text-center">RATE PER LTR</th>
              </tr>
            ) : (
              <>
                <tr className="border-b border-slate-200">
                  <th className="p-3 border-r border-slate-200 text-center align-middle" rowSpan={2}>DATE</th>
                  <th className="p-3 border-r border-slate-200 text-center align-middle bg-blue-50/50" rowSpan={2}>OP. BALANCE</th>
                  <th className="p-3 border-r border-slate-200 text-center" colSpan={tanks.length + 1}>INWARD</th>
                  <th className="p-3 border-r border-slate-200 text-center" colSpan={tanks.length + 1}>OUTWARD</th>
                  <th className="p-3 border-r border-slate-200 text-center align-middle bg-blue-100/50" rowSpan={2}>CLOSING</th>
                  <th className="p-3 text-center align-middle" rowSpan={2}>RATE PER LTR</th>
                </tr>
                <tr className="border-b border-slate-200 bg-slate-100">
                  {tanks.map(t => <th key={`in-${t.id}`} className="p-2 border-r border-slate-200 text-center whitespace-nowrap">{t.name}</th>)}
                  <th className="p-2 border-r border-slate-200 text-center bg-blue-50 font-extrabold text-blue-900">TOTAL</th>
                  {tanks.map(t => <th key={`out-${t.id}`} className="p-2 border-r border-slate-200 text-center whitespace-nowrap">{t.name}</th>)}
                  <th className="p-2 border-r border-slate-200 text-center bg-blue-50 font-extrabold text-blue-900">TOTAL</th>
                </tr>
              </>
            )}
          </thead>
          <tbody className="text-slate-600 font-medium">
            {statement.map((day) => {
              sumTotalInward += day.totalInward;
              sumTotalOutward += day.totalOutward;
              return (
                <tr key={day.date} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="p-2 border-r border-slate-200 text-center">{day.date}</td>
                  <td className="p-2 border-r border-slate-200 text-right bg-blue-50/30">{day.totalOpening.toFixed(2)}</td>
                  
                  {isSingleTank ? (
                    <>
                      <td className="p-2 border-r border-slate-200 text-right">{day.totalInward.toFixed(2)}</td>
                      <td className="p-2 border-r border-slate-200 text-right">{day.totalOutward.toFixed(2)}</td>
                    </>
                  ) : (
                    <>
                      {tanks.map(t => <td key={`in-${t.id}`} className="p-2 border-r border-slate-200 text-right">{day.inward[t.id].toFixed(2)}</td>)}
                      <td className="p-2 border-r border-slate-200 text-right font-bold bg-blue-50/30">{day.totalInward.toFixed(2)}</td>
                      {tanks.map(t => <td key={`out-${t.id}`} className="p-2 border-r border-slate-200 text-right">{day.outward[t.id].toFixed(2)}</td>)}
                      <td className="p-2 border-r border-slate-200 text-right font-bold bg-blue-50/30">{day.totalOutward.toFixed(2)}</td>
                    </>
                  )}
                  
                  <td className="p-2 border-r border-slate-200 text-right bg-blue-100/30 font-bold">{day.totalClosing.toFixed(2)}</td>
                  <td className="p-2 text-right text-slate-400">-</td>
                </tr>
              );
            })}

            {/* Handling Loss Row */}
            <tr className="bg-amber-50 font-bold text-slate-900 border-t-2 border-slate-300">
              <td colSpan={isSingleTank ? 2 : 2 + tanks.length} className="p-3 border-r border-slate-200 text-right">
                HANDLING LOSS ({(data.lossPercentage * 100).toFixed(2)}%)
              </td>
              <td className="p-3 border-r border-slate-200 text-right bg-amber-100/50">
                {sumTotalInward.toFixed(2)}
              </td>
              {!isSingleTank && <td colSpan={tanks.length} className="border-r border-slate-200 bg-amber-50"></td>}
              <td className="p-3 border-r border-slate-200 text-right bg-amber-100/50">
                {(sumTotalOutward + handlingLoss).toFixed(2)}
              </td>
              <td className="p-3 border-r border-slate-200 text-right bg-blue-200/50 text-blue-900">
                {(statement[statement.length - 1]?.totalClosing - handlingLoss).toFixed(2)}
              </td>
              <td className="p-3 bg-amber-50"></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TableProperties className="w-6 h-6 text-indigo-600" /> Government Stock Statement
          </h1>
          <p className="text-slate-500 text-sm mt-1">Preview ER-1 stock records and handling loss for the month.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select className="input-field py-2 bg-white" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <input type="number" className="input-field py-2 w-24 bg-white" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            <select className="input-field py-2" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
              <option value="Diesel">Diesel</option>
              <option value="Petrol">Petrol</option>
              <option value="Xtragreen">Xtragreen</option>
            </select>
            <input 
              type="number" 
              className="input-field py-2 w-36" 
              placeholder="Initial Balance" 
              value={openingBalance} 
              onChange={(e) => setOpeningBalance(e.target.value)} 
              title="Leave empty if previous month data exists"
            />
            <button onClick={handleExport} disabled={!data || isLoading} className="btn-primary py-2 px-4 flex items-center gap-2">
              <Download className="w-4 h-4"/> Download Excel
            </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="p-12 text-center text-slate-500 font-medium animate-pulse">Calculating physical stock and handling loss...</div>
      ) : (
        renderTable()
      )}
    </div>
  );
};

export default StockStatement;
